/* tslint:disable:template-cyclomatic-complexity max-file-line-count */

import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Geometry } from 'esri/geometry';
import { Subject } from 'rxjs';

import { LayerApi } from '../api/layer-api';
import { Callout } from '../classes/models/callout.model';
import { uuid } from '../classes/uuid';
import { IBasemapConfig } from '../components/mapComponents/basemap-switcher/basemap-config';
import { environment } from '../environment';
import { ESRI } from '../esri-loader-util';
import {
    IDomainValue,
    IGroup, ILayer, ILayerColumn, ILayerRMS, ILayerSettings, ILayerTableChild, ILayerTypes,
    IMapcoreDynamicLayer, IMapcoreFeatureLayer, IMapcoreFeatureProps, IMapcoreGraphicsLayer,
    IMapcoreGroupLayer, IMapcoreMapImageLayer, IMapcoreProps, IMapcoreSubLayer,
    IMapQueryConfiguration, IQuery, IRMSClipRange, ISaveableQuery, isCodedValueDomain, isFeatureLayer, isGraphicsLayer, isGroupLayer,
    isMapImageLayer,
    isPictureMarkerSymbol, isSymbolWithOutline, isSymbolWithSize, isSymbolWithWidth, LayerTypeId,
    LogLevel, MapCoreLayer, QueryableMapCoreLayer
} from '../types';

import { ILoggingService } from './interfaces/ILoggingService';
import { LegendService } from './legend.service';
import { SplitThemeService } from './split-theme.service';
import { ThemeService } from './theme.service';
import { ToSqlStringService } from './to-sql-string.service';
import { TooltipService } from './tooltip.service';
//import { Color } from '@progress/kendo-drawing';

let QueryTask: __esri.QueryTaskConstructor;
let Graphic: __esri.GraphicConstructor;
let GroupLayer: __esri.GroupLayerConstructor;

@Injectable({
    providedIn: 'root'
})
export class LayerService {
    private readonly AGGREGATE_LAYER_SUFFIXES = [' CENTER PT', ' BEGIN PT', ' END PT', ' PT', ' LN'];

    mapLayerType: number = LayerTypeId.Map;
    userLayerType: number = LayerTypeId.User;
    mapLayersSub: Subject<ILayerTypes> = new Subject<ILayerTypes>();
    public get mapLayers(): ILayerTypes {
        const mapLayers: ILayerTypes = { dynamic: [], feature: [], graphics: [], mapImage: [] };
        if (this.mapRef && this.mapRef.layers) {
            for (const layer of this.mapRef.layers.toArray() as (IMapcoreMapImageLayer[] | IMapcoreFeatureLayer[] | __esri.GroupLayer[])) {
                if (layer.type === 'group') {
                    for (const sub of (<__esri.GroupLayer>layer).layers.toArray() as (IMapcoreMapImageLayer[] | __esri.Layer[])) {
                        if (sub) {
                            if (sub.type === 'map-image') {
                                mapLayers.mapImage.push(sub as IMapcoreMapImageLayer);
                            } else {
                                mapLayers[sub.type].push(sub);
                            }
                        }
                    }
                } else {
                    if (layer.type === 'map-image') {
                        mapLayers.mapImage.push(layer as IMapcoreMapImageLayer);
                    } else {
                        mapLayers[layer.type].push(layer);
                    }
                }
            }
        }
        return mapLayers;
    }

    mapRef: __esri.Map;
    set mapView(mapView: __esri.MapView) {
        this._mapView = mapView;
        this.mapRef = mapView.map;
        this._mapView.watch('scale', this.onScaleChange.bind(this));
        const layers = mapView.map.allLayers;
        layers.on('change', (event) => this.onLayerChange(event));
        this.splitThemeService.mapView = this._mapView;
        this.themeService.mapView = this._mapView;
    }
    get mapView(): __esri.MapView {
        return this._mapView;
    }
    private _mapView: __esri.MapView;

    query: IQuery;
    polygonDefaultOpacity: number = 1;
    selectedLayer: IMapcoreSubLayer = null;

    public layersRefreshed = new EventEmitter<void>();

    constructor(private readonly layerApi: LayerApi,
        private readonly legendService: LegendService,
        private readonly splitThemeService: SplitThemeService,
        private readonly tooltipService: TooltipService,
        private readonly themeService: ThemeService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly http: HttpClient,
        @Inject('LoggingService') public LoggingService: ILoggingService) { }

    private onScaleChange(newScale: any, _oldVal: any) {
        const callouts = Callout.fromLayers(this.mapLayers.graphics);
        for (const callout of callouts) {
            callout.updateGeometry(newScale);
        }
    }

    onLayerChange(event: __esri.CollectionChangeEvent<__esri.Layer>): any {
        let nonWebtileLayers = false;
        if (event && event.added && event.added.length > 0) {
            for (const layer of event.added) {
                if (layer.type !== 'web-tile') {
                    nonWebtileLayers = true;
                    this.setUpLayerProperties(layer);
                }
            }
        } else if (event && (event.removed || event.moved)) {
            nonWebtileLayers = [...event.removed, ...event.moved].some(layer => layer.type !== 'web-tile');
        }

        if (nonWebtileLayers) {
            this.mapLayersSub.next(this.mapLayers);
        }
    }

    private setUpLayerProperties(layer) {
        switch (layer.type) {
            case 'feature':
                if (!layer.mapcoreProps) {
                    layer.mapcoreProps = {
                        name: layer.title
                    };

                }
                if (!layer.mapcoreProps.url && layer.url && layer.layerId) {
                    layer.mapcoreProps.url = `${layer.url}/${layer.layerId}`;
                }
                break;
            case 'map-image':
                if (layer.sublayers) {
                    for (const sublayer of layer.sublayers.toArray()) {
                        if (!sublayer.mapcoreProps) {
                            sublayer.mapcoreProps = {
                                id: sublayer.id,
                                name: sublayer.name
                            };
                        }
                    }
                }
                if (layer.mapcoreProps === undefined) {
                    layer.mapcoreProps = { symbols: '' };
                }
                layer.mapcoreProps.renderers = this.getRendererDefinitionsForLayer(layer);
                break;
            case 'dynamic':
                if (!layer.mapcoreProps) {
                    layer.mapcoreProps = {
                        id: layer.layerId,
                        name: layer.title,
                        url: layer.url
                    };
                }
                break;
            case 'graphics':
                if (layer.mapcoreProps === undefined) {
                    layer.mapcoreProps = {
                        id: layer.layerId,
                        hideInLegend: layer.hideInLegend
                    };
                }
                layer.mapcoreProps.name = layer.title ? layer.title : 'Graphic Layer';
                break;
            default:
                break;
        }
    }

    async convertToFeatureLayer(layer) {
        layer.convertingToFeatureLayer = true;
        //const result = await this.getLayerRecordCount(layer.sublayers.items[0].mapcoreProps.parentId);
        for (const sublayer of layer.sublayers.items) {
            const featureLayer = await sublayer.createFeatureLayer();
            featureLayer.mapcoreProps = layer.mapcoreProps;
            featureLayer.visible = layer.visible;
            featureLayer.opacity = layer.opacity;
            return featureLayer;
        }
    }

    public addLayer(layer: IMapcoreMapImageLayer | IMapcoreFeatureLayer | IMapcoreGraphicsLayer | IMapcoreDynamicLayer, index) {
        this.mapRef.add(layer, index);
    }

    public removeLayer(layer) {
        this.mapRef.layers.remove(layer);
    }
    public removeLayerById(layerId) {
        const layers = this.mapRef.layers.toArray();
        let index = -1;
        layers.forEach((layer, idx) => {
            if (layer.id === layerId) {
                return index = idx;
            }
        });
        if (index > -1) {
            this.mapRef.layers.removeAt(index);
        }
    }
    public getLayerIndex(currentLayer) {
        const layers = this.mapRef.layers.toArray();
        let index = -1;
        layers.forEach((layer, idx) => {
            if (layer.id === currentLayer.id) {
                return index = idx;
            }
        });
        return index;
    }
    async getLayersFromApi(layerIds: number[], props: string = null) {
        if (!props) {
            props = `id isSystem name labelingInfoJSON graphicsJSON renderersJSON description queryJSON systemQueryJSON parentId finalFilter typeId lastEditedDate hoverColumnList url originalMinScale originalMaxScale routingDetailJSON
                    featureEndpoints{url}
                    sources{sourceType}
                    tables{id geometryType name owner
                        metaTable{id}
                        spatialReference{wkt}
                        tableEditableColumns{id name displayOrder inputType isEditable isInsertable validationType tableId}}
                    columns {name alias databaseColumnOrder databaseDataLength databaseDataType columnIdentifierId, domainValueBasedFilter
                    columnIdentifier{name}}
                    categories(applicationId: ${environment.appId}){name}
                    permissions{id name action}`;
        }
        return this.layerApi.getLayers(layerIds, props);
    }

    async getLayerChildTables(layerId: number) {
        return this.layerApi.getLayerChildTables(layerId);
    }

    async getLayerChildTableRelationships(layerId: number) {

        return this.layerApi.getLayerChildRelationships(layerId, `id
		        childOwner
  	            parentLayerId
                parentPrimaryKeyColumnName
                layerChildRelationshipName
                childTableName
                childParentKeyColumnName`);
    }

    async getAllLayerChildTables() {
        return this.layerApi.getAllLayerChildTables();
    }

    async getLayerChildTable(layerId: number, tableId: number | number[], props: string) {
        return this.layerApi.getLayerChildTable(layerId, tableId, props);
    }

    async getLayerChildTableWithData(layerId: number, tableId: number | number[], fieldNames: string[], query: IQuery) {
        return this.layerApi.getLayerChildTableWithData(layerId, tableId, fieldNames, query);
    }
    async getTableRMSKeysByName(tableName) {
        const tableRMSKeys: ILayerRMS[] = await this.layerApi.getTableRMSKeysByName(tableName);
        if (tableRMSKeys.length > 0) {
            return tableRMSKeys[0];
        } else {
            return null;
        }
    }

    async getTableRMSKeys() {
        let tableRMSKeys: ILayerRMS[] = await this.layerApi.getTableRMSKeys();
        tableRMSKeys = tableRMSKeys.sort(function(a, b) {
            const nameA = a.layerDisplayName.toUpperCase();
            const nameB = b.layerDisplayName.toUpperCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }

            // names must be equal
            return 0;
        });
        return this.mergeIds(tableRMSKeys);
    }

    mergeIds(list) {
        const out: ILayerRMS[] = [];
        for (const entry of list) {
            const existingEntry = out.find(o => o.id === entry.id);
            if (existingEntry) {
                existingEntry.tableIds = `${existingEntry.tableId.toString()}, ${entry.tableId}`;
                existingEntry.tableName = `${existingEntry.tableName}, ${entry.tableName}`;
            } else {
                out.push(entry);
            }
        }

        return out;
    }

    async getLayerTableChildren(parentId: number | number[]) {
        return await this.layerApi.getLayerTableChildren(parentId);
    }

    async getTable(tableId: number, props: string) {
        return this.layerApi.getTable(tableId, props);
    }

    async getLayers(layerIds: number[]): Promise<MapCoreLayer[]> {
        const layersFromApi = await this.getLayersFromApi(layerIds);
        const resultLayers: MapCoreLayer[] = [];
        for (const layerFromApi of layersFromApi) {
            let layerSettings: ILayerSettings = {};
            layerSettings = await this.getUpdatedLayerSettings(layerSettings, layerFromApi);
            const mapcoreProps = await this.getMapcoreProps(layerSettings, layerFromApi);

            let sourceType: string;
            if (layerFromApi.sources.length > 0) {
                sourceType = layerFromApi.sources[0].sourceType;
            }
            switch (sourceType) {
                case 'DRAWING_DATA':
                // Not implemented
                case 'FEATURE_ENDPOINT':
                // Not implemented
                case 'LAYER_TABLE':
                    const url = this.layerApi.Api.http2.url + 'arcgis/mapserver';
                    const sublayers = await this.getSublayers(layerSettings, mapcoreProps);
                    const properties: __esri.MapImageLayerProperties = {
                        url,
                        sublayers,
                        visible: layerSettings.visible,
                        opacity: layerSettings.opacity
                    };
                    const [MapImageLayer] = await Promise.all([ESRI.MapImageLayer]);
                    const mapImageLayer = <IMapcoreMapImageLayer>new MapImageLayer(properties);
                    mapImageLayer.mapcoreProps = mapcoreProps;
                    this.tooltipService.setupMapImageLayerPopupTemplate(mapImageLayer);
                    resultLayers.push(mapImageLayer);
                    break;
                default:
                    break;
            }
        }
        return resultLayers;
    }

    async getTableColumns(tableId: number) {
        const props = `displayName
        columns {
            name
            dataType
            dataLength
        }
        metaTable { id }`;
        const result = await this.layerApi.getTable(tableId, props);
        if (result.length > 0) {
            result[0].columns = result[0].columns.filter(x => x.dataType !== 'SDO_GEOMETRY');
            result[0].columns = result[0].columns.filter(x => x.dataType !== 'BLOB');
            result[0].columns = result[0].columns.filter(x => x.dataType !== 'CLOB');
        }
        return result && result.length ? result[0] : null;
    }

    checkIfHeatmapIsOnOrOff = function(layer) {
        return layer.mapcoreProps.heatmapMode === 'on' || (layer.mapcoreProps.heatmapMode === 'auto' && this.mapView.zoom < 16);
    };

    private readonly dataTypeMap = {
        OID: 'NUMBER',
        STRING: 'VARCHAR2', // alternative: NVARCHAR2
        DOUBLE: 'FLOAT',
        INTEGER: 'NUMBER',
        'SMALL-INTEGER': 'NUMBER',
        DATE: 'DATE',
        'GLOBAL-ID': 'VARCHAR2',
        GUID: 'VARCHAR2'
        // additional types from DB: CLOB, CHAR, BLOB, TIMESTAMP(6), UNDEFINED
    };
    private setFeatureLayerColumns(featureLayer: IMapcoreFeatureLayer) {
        if (featureLayer && featureLayer.loaded) {
            featureLayer.mapcoreProps.columns = featureLayer.fields.filter(field => field.type.toUpperCase() !== 'GEOMETRY').map((field, i) => {
                let matchedType = this.dataTypeMap[field.type.toUpperCase()];
                if (!matchedType) {
                    console.warn(`Missing type map '${field.type}' for feature layer`);
                    matchedType = 'UNDEFINED';
                }
                let domainValues: IDomainValue[];
                if (field.domain) {
                    if (isCodedValueDomain(field.domain)) {
                        domainValues = field.domain.codedValues.map(val => ({
                            codeValue: val.code,
                            shortDescription: val.name,
                            longDescription: field.domain.name
                        } as IDomainValue));
                    } else {
                        console.warn(`Domain type '${field.domain.type}' not implemented`, field.domain);
                    }
                }
                return {
                    alias: field.alias,
                    databaseColumnOrder: i,
                    databaseDataType: matchedType,
                    name: field.name.toLowerCase(),
                    databaseDataLength: field.length,
                    domainValues
                } as ILayerColumn;
            });
        }
    }

    async addExternalLayersToMap(layerSettings: ILayerSettings, mapcoreProps: IMapcoreProps = null) {
        if (mapcoreProps === null) {
            mapcoreProps = { ...layerSettings };
        }
        mapcoreProps.isExternal = true;
        if (!layerSettings.url && layerSettings.featureEndpoints) {
            layerSettings.url = layerSettings.featureEndpoints[0].url;
            mapcoreProps.url = layerSettings.url;
        }
        const [FeatureLayer] = await Promise.all([ESRI.FeatureLayer]);
        const featureLayer = new FeatureLayer({
            url: layerSettings.url,
            title: layerSettings.name,
            visible: layerSettings.visible,
            minScale: mapcoreProps.originalMinScale,
            maxScale: mapcoreProps.originalMaxScale
        }) as IMapcoreFeatureLayer;
        if (!mapcoreProps.guid) {
            mapcoreProps.guid = uuid.v1(null, 0, 0);
        }
        if (mapcoreProps.renderers) {
            mapcoreProps.renderers = mapcoreProps.renderers.map(rend => this.fixRenderer(rend));
            featureLayer.renderer = mapcoreProps.renderers[0];
            if (mapcoreProps.renderers[0].symbol && mapcoreProps.renderers[0].symbol.type === 'picture-marker') {
                this.fixPictureSymbols([(featureLayer.renderer as __esri.SimpleRenderer).symbol]);
            }
        }
        featureLayer.mapcoreProps = mapcoreProps;
        let queried = false;
        if (mapcoreProps.columns && mapcoreProps.query) {
            queried = true;
            featureLayer.definitionExpression = await this.toSqlStringService.getSql(mapcoreProps.query, mapcoreProps.columns);
        }
        featureLayer.when(async () => {
            this.setFeatureLayerColumns(featureLayer);
            if (!queried && mapcoreProps.query) {
                featureLayer.definitionExpression = await this.toSqlStringService.getSql(mapcoreProps.query, mapcoreProps.columns);
            }
            if (layerSettings.zoomToLayer) {
                await this.zoomToLayer(featureLayer.id);
            }
        });
        this.tooltipService.setupFeatureLayerPopupTemplate(featureLayer);
        this.mapRef.add(featureLayer);
        this.mapLayersSub.next(this.mapLayers);
        return featureLayer;
    }

    async addFeatureSetLayerToMap(layers: IMapcoreFeatureProps[]): Promise<any> {

        let layerConstructor: __esri.FeatureLayerConstructor;
        let simpleRenderer: __esri.SimpleRendererConstructor;
        [layerConstructor, simpleRenderer] = await Promise.all([ESRI.FeatureLayer, ESRI.SimpleRenderer]);

        const loadingPromises: Promise<any>[] = [];
        let lastColor, lastLayerPrefix;

        for (const layer of layers) {
            const layerPrefix = this.getLayerPrefix(layer.title);
            const color = layerPrefix !== lastLayerPrefix
                ? this.legendService.getNewColor(this.mapView.map)
                : lastColor;

            layer.renderer = new simpleRenderer({
                symbol: this.themeService.getSymbolDefinitionFromGeometry(layer.source[0].geometry.type, color)
            });
            const featureLayer = new layerConstructor(layer);
            loadingPromises.push(featureLayer.when());
            this.mapRef.layers.push(featureLayer);

            lastColor = color;
            lastLayerPrefix = layerPrefix;
        }

        return Promise.all(loadingPromises);
    }

    private getLayerPrefix(title: string) {
        for (const suffix of this.AGGREGATE_LAYER_SUFFIXES) {
            if (title.endsWith(suffix)) {
                return title.substring(0, title.indexOf(suffix));
            }
        }

        return title;
    }

    async addMapcoreLayersToMap(layerSettingsArray: ILayerSettings[], query: IQuery = null, zoomToLayer: boolean = false, generateNewLayer: boolean = true, paramConfig?: IMapQueryConfiguration[]) {
        const layerIds = layerSettingsArray.map(x => x.id);
        const layersFromApi = await this.getLayersFromApi(layerIds);
        const resultingEsriLayers: MapCoreLayer[] = [];
        for (const currentLayerSetting of layerSettingsArray) {
            const layerFromDatabase = layersFromApi.filter(x => x.id === currentLayerSetting.id)[0];
            if (layerFromDatabase) {
                if (!currentLayerSetting.query) {
                    currentLayerSetting.query = query;
                }
                if (currentLayerSetting.generateNewLayer === undefined) {
                    currentLayerSetting.generateNewLayer = generateNewLayer;
                }
                if (currentLayerSetting.zoomToLayer === undefined) {
                    currentLayerSetting.zoomToLayer = zoomToLayer;
                }
                let sourceType: string;
                if (layerFromDatabase.sources && layerFromDatabase.sources.length > 0) {
                    sourceType = layerFromDatabase.sources[0].sourceType;
                }
                const updatedLayerSettings: ILayerSettings = await this.getUpdatedLayerSettings(currentLayerSetting, layerFromDatabase);
                const mapcoreProps = await this.getMapcoreProps(currentLayerSetting, layerFromDatabase);

                let resultingEsriLayer: MapCoreLayer;
                switch (sourceType) {
                    case 'FEATURE_ENDPOINT':
                        resultingEsriLayer = await this.addExternalLayersToMap(updatedLayerSettings, mapcoreProps);
                        break;
                    case 'DRAWING_DATA':
                        resultingEsriLayer = await this.addSavedGraphicsLayer(updatedLayerSettings, layerFromDatabase, mapcoreProps);
                        break;
                    case 'LAYER_TABLE':
                        if (paramConfig) {
                            this.applyCyfyLayerFilterTransform(paramConfig, mapcoreProps);
                            this.applyCurrentFYTransformation(mapcoreProps);
                        }
                        resultingEsriLayer = await this.addMapcoreLayer(updatedLayerSettings, mapcoreProps);
                        break;
                    default:
                        break;
                }
                resultingEsriLayers.push(resultingEsriLayer);
            }
        }
        resultingEsriLayers.push(...(await this.groupLayers(resultingEsriLayers)));
        return resultingEsriLayers;
    }
    async convertToImagelayer(layer: IMapcoreFeatureLayer) {
        let layerSettings: ILayerSettings = null;
        const index = this.getLayerIndex(layer);
        if (layer.mapcoreProps) {
            const mapcoreProps = layer.mapcoreProps as IMapcoreProps;
            layerSettings = { id: mapcoreProps.parentId, query: mapcoreProps.query, name: mapcoreProps.name, visible: layer.visible, opacity: layer.opacity };
        }
        const mapImageLayer = await this.addMapcoreLayer(layerSettings, layer.mapcoreProps, index);
        mapImageLayer.mapcoreProps.url = undefined;
        mapImageLayer.sublayers.forEach(sublayer => {
            sublayer.renderer = layer.mapcoreProps.renderer;
        });
        return mapImageLayer;

    }

    async groupLayers(layers: MapCoreLayer[]) {
        const groupedLayers = layers.filter(layer => layer.mapcoreProps.groupParent).reduce<{ [key: string]: MapCoreLayer[] }>((acc, layer) => {
            const key = layer.mapcoreProps.groupParent.groupId ? `id:${layer.mapcoreProps.groupParent.groupId}` : layer.mapcoreProps.groupParent.name;
            if (acc[key]) {
                acc[key].push(layer);
            } else {
                acc[key] = [layer];
            }
            return acc;
        }, {});
        const groupLayers: IMapcoreGroupLayer[] = [];
        if (Object.keys(groupedLayers).length) {
            GroupLayer = await ESRI.GroupLayer;
            const existingGroups = this.mapRef.layers.filter(layer => layer.type === 'group').toArray() as IMapcoreGroupLayer[];
            for (const group in groupedLayers) {
                if (groupedLayers.hasOwnProperty(group)) {
                    const gl = groupedLayers[group];
                    const groupData: IGroup = gl[0].mapcoreProps.groupParent;
                    let groupLayer: IMapcoreGroupLayer;
                    if (group.startsWith('id:')) {
                        const groupId = group.replace('id:', '');
                        groupLayer = existingGroups.find(egl => egl.id === groupId);
                    }
                    if (!groupLayer) {
                        const splitTheme = groupData.splitThemeJSON && JSON.parse(groupData.splitThemeJSON);
                        groupLayer = new GroupLayer({
                            title: groupData.name,
                            visible: true
                        }) as IMapcoreGroupLayer;
                        groupLayer.mapcoreProps = { name: groupData.name, isGroupLayerExpanded: groupData.isOpen, splitTheme };
                        this.mapRef.add(groupLayer);
                        for (const layer of gl) {
                            layer.mapcoreProps.groupParent.groupId = groupLayer.id;

                            if (splitTheme) {
                                layer.mapcoreProps.splitTheme = splitTheme;
                            }
                        }
                    }
                    groupLayer.addMany(gl);
                    groupLayers.push(groupLayer);

                    if (groupLayer.mapcoreProps.splitTheme) {
                        await this.splitThemeService.rehydrateTheme(groupLayer);
                    }
                }
            }
        }
        return groupLayers;
    }

    async addMapcoreLayer(layerSettings: ILayerSettings, mapcoreProps: IMapcoreProps = null, index = -1) {
        if (!mapcoreProps) {
            mapcoreProps = layerSettings;
        }
        const url = this.layerApi.Api.http2.url + 'arcgis/mapserver';
        let holdRenderers;
        if (mapcoreProps.renderers) {
            mapcoreProps.renderers = mapcoreProps.renderers.map(rend => this.fixRenderer(rend));
            if (mapcoreProps.renderers.some(ren => ren.type === 'unique-value' || ren.type === 'class-breaks')) {
                holdRenderers = mapcoreProps.renderers;
                const visualVariable = await ESRI.VisualVariable;
                if (mapcoreProps.renderers.length > 0 && mapcoreProps.renderers[0].visualVariables) {
                    holdRenderers[0].visualVariables = mapcoreProps.renderers[0].visualVariables.map(v => v = visualVariable.fromJSON(v));
                }
                delete mapcoreProps.renderers;
                delete layerSettings.renderers;
            }
        }
        const sublayers = await this.getSublayers(layerSettings, mapcoreProps);
        if (holdRenderers && holdRenderers.length > 0 && holdRenderers[0].visualVariables) {
            sublayers.forEach(sl => (sl.renderer as any).visualVariables = holdRenderers[0].visualVariables);
        } else if (mapcoreProps.renderers && (mapcoreProps.renderers as any).visualVariables) {
            sublayers.forEach(sl => (sl.renderer as any).visualVariables = (mapcoreProps.renderers as any).visualVariables);
        }
        const properties: any = {
            url,
            sublayers,
            visible: layerSettings.visible,
            opacity: layerSettings.opacity,
            minScale: mapcoreProps.originalMinScale,
            maxScale: mapcoreProps.originalMaxScale
        };
        if (layerSettings.esriLayerId) {
            properties.id = layerSettings.esriLayerId;
        }
        const handle = this.mapView.map.allLayers.on('change', (event: __esri.CollectionChangeEvent) => {
            handle.remove();
            const layer = event.added[0] as IMapcoreMapImageLayer;
            event.added.shift();

            layer.on('layerview-destroy', () => this.themeService.forgetTheme(layer));

            if (holdRenderers) {
                setTimeout(async () => {
                    layer.mapcoreProps.simpleRenderers = this.getRendererDefinitionsForLayer(layer);
                    layer.mapcoreProps.renderers = layer.sublayers.map(sl => sl.renderer.toJSON()).toArray();
                    if (holdRenderers[0].visualVariables) {
                        layer.mapcoreProps.renderers.forEach(rend => rend.visualVariables = holdRenderers[0].visualVariables);
                        layer.mapcoreProps.simpleRenderers.forEach(rend => rend.visualVariables = holdRenderers[0].visualVariables);
                    }

                    layer.sublayers.forEach((sl, i) => sl.renderer = holdRenderers[i]);
                    this.themeService.saveTheme(layer, holdRenderers[0]);
                    layer.mapcoreProps.isThemed = true;

                    try {
                        await this.themeService.updateLayerTheme(layer);
                    } catch (er) {
                        if ((<Error>er).message) {
                            this.LoggingService.Log('Layer Theme cannot be updated', er, LogLevel.Warn, false);
                        }
                    }
                }, 10);
            }
        });
        const mapImageLayer = await this.addEsriMapImagelayer(properties, mapcoreProps, index);
        if (layerSettings.zoomToLayer) {
            await this.zoomToLayer(mapImageLayer.id);
        }
        return mapImageLayer;
    }

    setIsPetl(mapcoreProps: IMapcoreProps) {
        if (mapcoreProps &&
            ((mapcoreProps.tableNames && mapcoreProps.tableNames.some(table => table.toUpperCase().includes('RMS_PETL')))
                || (mapcoreProps.tables && mapcoreProps.tables.some(table => table.name && table.name.toUpperCase().includes('RMS_PETL'))))) {
            mapcoreProps.isPETL = true;
        }
    }

    public async addEsriMapImagelayer(mapImageProperties: __esri.MapImageProperties, mapcoreProps: IMapcoreProps = null, index = -1) {
        const [MapImageLayer] = await Promise.all([ESRI.MapImageLayer]);
        const mapImageLayer = new MapImageLayer(mapImageProperties) as IMapcoreMapImageLayer;
        if (index > -1) {
            this.mapRef.add(mapImageLayer, index);
        } else {
            this.mapRef.add(mapImageLayer);
        }

        if (mapcoreProps) {
            mapImageLayer.mapcoreProps = mapcoreProps;
        }
        this.tooltipService.setupMapImageLayerPopupTemplate(mapImageLayer);
        return mapImageLayer;
    }

    public async zoomToLayer(layerId: string | string[], zoomThreshold: number = 14) {
        if (typeof layerId === 'string') {
            const layer = this.getExistingEsriLayerById(layerId) as __esri.Layer;
            const layerExtent = await this.getLayerExtent(layer);
            if (await this.validExtent(layerExtent)) {
                this.zoomToTarget(layerExtent, undefined, zoomThreshold);
            }
        } else {
            const extents: __esri.Extent[] = [];
            for (const id of layerId) {
                const layer = this.getExistingEsriLayerById(id) as __esri.Layer;
                const layerExtent = await this.getLayerExtent(layer);
                if (await this.validExtent(layerExtent)) {
                    extents.push(layerExtent as __esri.Extent);
                }
            }
            if (extents.length) {
                const totalExtent = extents.reduce((a, b) => a.union(b));
                this.zoomToTarget(totalExtent, undefined, zoomThreshold);
            }
        }
    }

    public async getLayerExtent(layer: __esri.Layer) {
        QueryTask = await ESRI.QueryTask;
        let Extent: __esri.ExtentConstructor;
        [Extent] = await Promise.all([ESRI.Extent]);
        return new Promise<__esri.Geometry[] | __esri.Extent>((resolve, reject) => {
            try {
                layer.when(async () => {
                    if (isGraphicsLayer(layer)) {
                        const extents = layer.graphics.toArray().map(graphic => {
                            let ext: __esri.Extent;
                            if (graphic.geometry.type === 'point') {
                                const p = <__esri.Point>graphic.geometry;
                                ext = new Extent({ xmax: p.x, xmin: p.x, ymax: p.y, ymin: p.y, spatialReference: p.spatialReference });
                            } else {
                                ext = graphic.geometry.extent.clone();
                            }
                            return ext;
                        });
                        const extent = extents.reduce((a, b) => a.union(b));
                        resolve(extent);
                    } else if (isMapImageLayer(layer)) {
                        const queryPromises: IPromise<{ count: number; extent: __esri.Extent }>[] = [];
                        layer.sublayers.forEach(sublayer => {
                            const clonedSublayer = sublayer.clone();
                            const task = new QueryTask({ url: clonedSublayer.url });
                            queryPromises.push(task.executeForExtent(clonedSublayer.createQuery()));
                        });
                        const responses = await Promise.all(queryPromises);
                        let extent: __esri.Extent = responses.pop().extent;
                        for (const res of responses) {
                            // Ignore empty extents unless that is all there is
                            if (await this.validExtent(extent)) {
                                if (await this.validExtent(res.extent)) {
                                    extent.union(res.extent);
                                }
                            } else {
                                extent = res.extent;
                            }
                        }
                        resolve(extent);
                    } else if (isFeatureLayer(layer)) {
                        const query = layer.createQuery();
                        query.outSpatialReference = this.mapView.spatialReference.clone();
                        const response: { count: number; extent: __esri.Extent } = await layer.queryExtent(query);
                        resolve(response.extent);
                    } else {
                        resolve(layer.fullExtent);
                    }
                });
            } catch (er) {
                reject(er);
            }
        });
    }
    public async getLayerCopyRight(basemapConfig: IBasemapConfig) {
        if (!basemapConfig.copyrightText && basemapConfig.tileUrl && (basemapConfig.tileUrl.toLowerCase().indexOf('mapserver') > -1 || basemapConfig.tileUrl.toLowerCase().indexOf('imageserver') > -1)) {
            const serviceUrl = `${basemapConfig.tileUrl.substr(0, basemapConfig.tileUrl.toLowerCase().indexOf('tile'))}?f=pjson`;
            try {
                const result = await this.http.get<any>(serviceUrl).toPromise();
                basemapConfig.copyrightText = result.copyrightText || null;
            } catch {
                //do nothing to the copyright
            }
        }
    }

    public async validExtent(extent: __esri.Geometry[] | __esri.Extent) {
        let Extent: __esri.ExtentConstructor;
        [Extent] = await Promise.all([ESRI.Extent]);
        if (extent instanceof Extent) {
            return Boolean(extent.xmax || extent.xmin || extent.ymax || extent.ymin);
        } else {
            return true;
        }
    }

    async copyEsriLayer(esriLayer: any) {
        if (esriLayer.mapcoreProps) {
            const mapcoreProps = esriLayer.mapcoreProps as IMapcoreProps;
            const layerSettings: ILayerSettings = { id: mapcoreProps.parentId, query: mapcoreProps.query, name: mapcoreProps.name, parentQuery: esriLayer.mapcoreProps.parentQuery };
            return await this.addMapcoreLayersToMap([layerSettings]);
        }
        return [];
    }

    async copyLayerInternal(layerId) {
        const foundLayer = this.getEsriLayerByUniqueId(layerId);
        const url = this.layerApi.Api.http2.url + 'arcgis/MapServer';
        let layerConstructor: __esri.MapImageLayerConstructor;
        [layerConstructor] = await Promise.all([ESRI.MapImageLayer, ESRI.SpatialReference]);
        const subLayers = await this.getSublayers(foundLayer.mapcoreProps);
        const dynamicMapServiceLayer = new layerConstructor({
            url,
            sublayers: subLayers,
            visible: foundLayer.visible ? foundLayer.visible : false

        });
        (<any>dynamicMapServiceLayer).mapcoreProps = {};
        Object.assign((<any>dynamicMapServiceLayer).mapcoreProps, foundLayer.mapcoreProps);
        return dynamicMapServiceLayer;
    }

    async createClippedRMSGeom(clipRanges: IRMSClipRange | IRMSClipRange[]) {
        return this.layerApi.createClippedRMSGeom(clipRanges);
    }

    async addMapcoreLayerIdsToMap(layerIds: number[]) {
        const layerReferences = layerIds.map(x => ({ id: x }));
        return await this.addMapcoreLayersToMap(layerReferences);
    }

    async getSublayers(layerSettings: ILayerSettings, mapcoreProps: IMapcoreProps = null): Promise<IMapcoreSubLayer[]> {
        if (!mapcoreProps) {
            mapcoreProps = layerSettings;
        }
        let SpatialReference: __esri.SpatialReferenceConstructor;
        let Sublayer: __esri.SublayerConstructor;
        [SpatialReference, Sublayer] = await Promise.all([ESRI.SpatialReference, ESRI.Sublayer]);
        const sublayers = [];
        let spatialReference = new SpatialReference({ wkid: 102100 });
        if (mapcoreProps.tables && mapcoreProps.tables[0].spatialReference && mapcoreProps.tables[0].spatialReference.wkt) {
            spatialReference = new SpatialReference({ wkt: mapcoreProps.tables[0].spatialReference.wkt });
        }
        const sublayerDefinitions = await this.getSublayerDefinitionsFromMapcoreProps(layerSettings, mapcoreProps);
        for (const sublayerDefinition of sublayerDefinitions) {
            (<__esri.QueryTableDataSource>(<__esri.DynamicDataLayer>sublayerDefinition.source).dataSource).spatialReference = spatialReference;
            const newSub = new Sublayer(sublayerDefinition) as IMapcoreSubLayer;
            newSub.mapcoreProps = mapcoreProps;
            sublayers.push(newSub);
        }
        return sublayers;
    }

    private async getUpdatedLayerSettings(layerSettings: ILayerSettings, layerFromDatabase: ILayer) {
        layerSettings.id = layerFromDatabase.id;
        layerSettings.isSystem = layerFromDatabase.isSystem;
        layerSettings.name = layerSettings.name ? layerSettings.name : layerFromDatabase.name;
        layerSettings.description = layerFromDatabase.description ? layerFromDatabase.description : 'No description available';
        layerSettings.columns = layerSettings.columns ? layerSettings.columns : layerFromDatabase.columns;
        layerSettings.hoverColumnList = layerSettings.hoverColumnList ? layerSettings.hoverColumnList : layerFromDatabase.hoverColumnList;
        layerSettings.sources = layerFromDatabase.sources;
        layerSettings.geometryTypes = layerFromDatabase.tables ? layerFromDatabase.tables.map(x => x.geometryType) : (<any>layerFromDatabase).geometryTypes;
        this.formatGeometryTypes(layerSettings);
        layerSettings.parentId = layerFromDatabase.parentId ? layerFromDatabase.parentId : null;
        layerSettings.typeId = layerFromDatabase.typeId;
        layerSettings.visible = layerSettings.visible === undefined ? true : layerSettings.visible;
        layerSettings.originalMinScale = layerSettings.originalMinScale ? layerSettings.originalMinScale : layerFromDatabase.originalMinScale;
        layerSettings.originalMaxScale = layerSettings.originalMaxScale ? layerSettings.originalMaxScale : layerFromDatabase.originalMaxScale;
        const defaultOpacity = 1;
        if (layerFromDatabase.isSystem && layerSettings.opacity === undefined && layerSettings.geometryTypes[0] === 'polygon') {
            layerSettings.opacity = this.polygonDefaultOpacity;
        } else if (layerSettings.opacity === undefined) {
            layerSettings.opacity = defaultOpacity;
        }
        if (layerFromDatabase.featureEndpoints && layerFromDatabase.featureEndpoints.length) {
            layerSettings.url = layerFromDatabase.featureEndpoints[0].url;
        }
        if (layerFromDatabase.labelingInfoJSON) {
            layerSettings.labelingInfo = JSON.parse(layerFromDatabase.labelingInfoJSON);
        }
        if (layerFromDatabase.routingDetailJSON) {
            layerSettings.routingDetail = JSON.parse(layerFromDatabase.routingDetailJSON);
        }
        await this.layerSettingsRenderersSetup(layerSettings, layerFromDatabase);
        return layerSettings;
    }

    private async layerSettingsRenderersSetup(layerSettings: ILayerSettings, layerFromDatabase: ILayer) {
        if (!layerSettings.renderers) {
            if (layerFromDatabase.renderers && !Array.isArray(layerFromDatabase.renderers)) {
                layerSettings.renderers = JSON.parse(layerFromDatabase.renderers);
            } else if (layerFromDatabase.renderersJSON) {
                layerSettings.renderers = JSON.parse(layerFromDatabase.renderersJSON);
            } else if (layerSettings.renderersJSON) {
                layerSettings.renderers = JSON.parse(layerSettings.renderersJSON);
            }
            await this.setupRenderers(layerSettings.renderers);
            if (layerSettings.renderers && layerSettings.renderers.length) {
                for (let i = layerSettings.renderers.length - 1; i >= 0; i--) {
                    const renderer = layerSettings.renderers[i];
                    if (renderer.type && renderer.type === 'opacity') {
                        layerSettings.opacity = renderer.value;
                        layerSettings.renderers.splice(i, 1);
                    }
                }
            }
        }
    }

    private async setupRenderers(renderers: any[]) {
        if (renderers) {
            const SimpleRenderer = await ESRI.SimpleRenderer;
            for (let i = renderers.length - 1; i >= 0; i--) {
                const renderer = renderers[i];
                if (renderer.symbol && renderer.symbol.type.startsWith('esri')) {
                    // ESRI's toJSON doesn't save a color to a line symbol even though fromJSON needs it
                    if (!renderer.symbol.color && renderer.symbol.outline && renderer.symbol.outline.color) {
                        renderer.symbol.color = renderer.symbol.outline.color;
                    }
                    renderers[i] = SimpleRenderer.fromJSON(renderer);
                }
            }
        }
    }

    private async getMapcoreProps(layerSettings: ILayerSettings, layerFromDatabase: ILayer) {
        const mapcoreProps = this.setMapcorePropsObject(layerSettings, layerFromDatabase);
        this.setIsPetl(mapcoreProps);
        //validating query properly in all scenarios
        this.generateMapcorePropsQuery(mapcoreProps, layerSettings, layerFromDatabase);
        this.formatGeometryTypes(mapcoreProps);

        if (layerFromDatabase.tables && layerFromDatabase.tables[0] && layerFromDatabase.tables[0].metaTable) {
            mapcoreProps.metaTableId = layerFromDatabase.tables[0].metaTable.id;
        }

        // If system layer, there won't be a parentId so map/user layer that will be created will have parentId as the sytem layer's Id
        // Otherwise, get Id from database to use for possible layer update (does not apply to system layer since it cannot be updated)
        if (layerFromDatabase.isSystem) {
            mapcoreProps.id = null;
            mapcoreProps.parentId = layerFromDatabase.id;
        } else {
            mapcoreProps.idFromSavedLayer = layerFromDatabase.id;

            if (layerSettings.generateNewLayer) {
                mapcoreProps.idFromSavedMap = layerFromDatabase.id;
                mapcoreProps.id = null;
            }
        }

        mapcoreProps.parentQuery = layerSettings.parentQuery ? layerSettings.parentQuery : JSON.parse(layerFromDatabase.systemQueryJSON);

        if (layerFromDatabase.featureEndpoints && layerFromDatabase.featureEndpoints.length) {
            mapcoreProps.url = layerFromDatabase.featureEndpoints[0].url;
        }

        mapcoreProps.renderers = layerSettings.renderers;

        if (layerSettings.exportJoinFilterParams) {
            mapcoreProps.exportJoinFilterParams = layerSettings.exportJoinFilterParams;
        }

        await this.setColumns(layerSettings, mapcoreProps, layerFromDatabase);

        if (layerSettings && layerSettings.geometryString) {
            mapcoreProps.geometryString = layerSettings.geometryString;
        }

        this.setSpatialParams(mapcoreProps);

        if (layerSettings && layerSettings.splitThemeLabel) {
            mapcoreProps.splitThemeLabel = layerSettings.splitThemeLabel;
        }

        return mapcoreProps;
    }

    private generateMapcorePropsQuery(mapcoreProps: IMapcoreProps, layerSettings: ILayerSettings, layerFromDatabase: ILayer) {
        mapcoreProps.query = {
            childTableRecord: null,
            childTableQuery: {},
            joinRecord: null,
            joinTableQuery: {},
            operator: '',
            field: '',
            value: '',
            subqueries: []
        };
        let queryAssigned = false;
        if (this.isProperQuery(layerSettings.query)) {
            mapcoreProps.query = layerSettings.query;
            queryAssigned = true;
        }
        if (layerSettings.query && layerSettings.query.childTableRecord) {
            mapcoreProps.query.childTableRecord = layerSettings.query.childTableRecord;
            if (this.isProperQuery(layerSettings.query.childTableQuery)) {
                mapcoreProps.query.childTableQuery = layerSettings.query.childTableQuery;
            }
            queryAssigned = true;
        }
        if (layerSettings.query && layerSettings.query.joinRecord) {
            mapcoreProps.query.joinRecord = layerSettings.query.joinRecord;
            if (this.isProperQuery(layerSettings.query.joinTableQuery)) {
                mapcoreProps.query.joinTableQuery = layerSettings.query.joinTableQuery;
            }
            queryAssigned = true;
        }
        if (!queryAssigned && layerFromDatabase.queryJSON && (layerFromDatabase.typeId === this.userLayerType || layerFromDatabase.typeId === this.mapLayerType)) {
            mapcoreProps.query = JSON.parse(layerFromDatabase.queryJSON);
            if (mapcoreProps.query && mapcoreProps.query.parentQuery) {
                delete mapcoreProps.query.parentQuery;
            }
            queryAssigned = true;
        }
        if (!queryAssigned || !mapcoreProps.query) {
            mapcoreProps.query = {};
        }
    }

    private async setColumns(layerSettings: ILayerSettings, mapcoreProps: IMapcoreProps, layerFromDatabase: ILayer) {
        if (layerSettings.joinColumns) {
            mapcoreProps.joinColumns = layerSettings.joinColumns;
        }
        if (layerSettings.childColumns) {
            mapcoreProps.childColumns = layerSettings.childColumns;
        }
        if (mapcoreProps.isPETL) {
            const petlQueries = this.toSqlStringService.getClauses(mapcoreProps.query, { field: 'project_no', operator: '=' });
            if (petlQueries && petlQueries.length) {
                const columns = await this.layerApi.getPetlProjectColumnAliases(parseInt(petlQueries[0].value, 10));
                if (columns && columns.length) {
                    for (const existingColumn of mapcoreProps.columns) {
                        const matchingPetlCol = columns.find(col => col.columnName.toLowerCase().trim() === existingColumn.name.toLowerCase().trim());
                        if (matchingPetlCol) {
                            existingColumn.alias = matchingPetlCol.aliasName;
                        }
                    }
                }
            }
        } else if (layerFromDatabase.tables && layerFromDatabase.tables.some(table => !!table.tableEditableColumns)) {
            mapcoreProps.tableEditableColumns = layerFromDatabase.tables.map(table => table.tableEditableColumns).flat();
            mapcoreProps.tableEditableColumns.forEach(tec => {
                if (typeof tec.inputType === 'string') {
                    tec.inputType = JSON.parse(tec.inputType);
                }
            });
        }
    }

    public isProperQuery(query: IQuery) {
        if (query && query.subqueries) {
            let result = true;
            query.subqueries.forEach(subQuery => {
                result = result && this.isProperQuery(subQuery);
            });
            return result;
        } else {
            return query && (query.field && query.operator && (query.value !== null || query.operator.toLowerCase().includes('null')));
        }
    }

    private setMapcorePropsObject(layerSettings: ILayerSettings, layerFromDatabase: ILayer) {
        return {
            columns: layerSettings.columns,
            hoverColumnList: layerSettings.hoverColumnList,
            id: layerFromDatabase.id,
            hoverEnabled: layerSettings.hoverEnabled,
            identifyEnabled: layerSettings.identifyEnabled,
            identifyNonVisibleEnabled: layerSettings.identifyNonVisibleEnabled,
            isSystem: layerFromDatabase.isSystem,
            isAOI: layerSettings.isAOI,
            name: layerSettings.name || layerFromDatabase.name,
            description: layerFromDatabase.description,
            groupParent: layerSettings.groupParent,
            sources: layerFromDatabase.sources,
            spatialQueryParams: layerSettings.spatialQueryParams,
            geometryTypes: layerFromDatabase.tables ? layerFromDatabase.tables.map(x => x.geometryType) : (<any>layerFromDatabase).geometryTypes,
            parentId: layerFromDatabase.parentId ? layerFromDatabase.parentId : null,
            tableNames: layerFromDatabase.tables ? layerFromDatabase.tables.map(t => t.name) : (<any>layerFromDatabase).tableNames,
            typeId: layerFromDatabase.typeId,
            visible: layerSettings.visible,
            tables: layerFromDatabase.tables,
            category: layerFromDatabase.categories ? layerFromDatabase.categories.map(c => c.name).join(', ') : null,
            labelingInfo: layerSettings.labelingInfo,
            lastEditedDate: layerFromDatabase.lastEditedDate,
            hideInLegend: layerSettings.hideInLegend !== undefined ? layerSettings.hideInLegend : undefined,
            permissions: layerFromDatabase.permissions,
            originalMinScale: layerFromDatabase.originalMinScale,
            originalMaxScale: layerFromDatabase.originalMaxScale,
            routingDetail: layerSettings.routingDetail
        } as IMapcoreProps;
    }

    private setSpatialParams(mapcoreProps: IMapcoreProps) {
        if (!mapcoreProps.spatialQueryParams && mapcoreProps.query && mapcoreProps.query.spatialQueryParams) {
            mapcoreProps.spatialQueryParams = mapcoreProps.query.spatialQueryParams;
            delete mapcoreProps.query.spatialQueryParams;
        }
    }

    formatGeometryTypes(mapcoreProps: IMapcoreProps) {
        const p = 'point';
        mapcoreProps.geometryTypes = mapcoreProps.geometryTypes.map(x => x.toLowerCase().trim());
        if (mapcoreProps.geometryTypes.length > 1 && mapcoreProps.geometryTypes.includes(p)) {
            mapcoreProps.geometryTypes.sort((a, b) => a === p ? -1 : b === p ? 1 : 0);
        }
    }

    private async getSublayerDefinitionsFromMapcoreProps(layerSettings: ILayerSettings, mapcoreProps: IMapcoreProps) {
        const sublayerDefinitions: ISimpleRendererSublayerProperties[] = [];
        await this.setupRenderers(layerSettings.renderers);
        for (const geometryType of mapcoreProps.geometryTypes) {
            const geometryTypeModified = geometryType.replace('line', 'polyline');
            const workspaceId = mapcoreProps.parentId;
            const keepColor = mapcoreProps.geometryTypes.indexOf(geometryType) !== 0 && mapcoreProps.geometryTypes.length > 1;
            const renderer = this.themeService.getRenderDefinitionForGeometryType(geometryType, keepColor, layerSettings.renderers) as any;
            let finalQuery = { ...mapcoreProps.query };
            if (mapcoreProps.parentQuery) {
                finalQuery = this.toSqlStringService.addFilter({ ...mapcoreProps.query }, mapcoreProps.parentQuery);
            }
            if (!finalQuery) {
                finalQuery = {};
            }
            const sublayerDef = {
                title: mapcoreProps.name,
                renderer,
                visible: true,
                source: {
                    type: 'data-layer',
                    dataSource: {
                        type: 'query-table',
                        geometryType: geometryTypeModified,
                        workspaceId: workspaceId.toString(),
                        query: JSON.stringify(finalQuery),
                        oidFields: this.getObjectIdColumnName(mapcoreProps)
                    },
                    fields: null
                },
                labelingInfo: mapcoreProps.labelingInfo
            } as ISimpleRendererSublayerProperties;
            sublayerDefinitions.push(sublayerDef);
        }
        if (!layerSettings.renderers) {
            this.setSublayerColors(sublayerDefinitions);
        }
        return sublayerDefinitions;
    }

    setSublayerColors(sublayerDefinitions: any[]) {
        let color;
        for (const definition of sublayerDefinitions.filter(sld =>
            sld.renderer.type !== 'unique-value'
            && sld.renderer.type !== 'uniqueValue'
            && sld.renderer.type !== 'class-breaks'
            && sld.renderer.type !== 'classBreaks')) {
            if (!color) {
                color = definition.renderer.symbol.color;
            } else {
                definition.renderer.symbol.color = color;
            }
        }
    }

    async updateLayerFilter(esriLayer: QueryableMapCoreLayer, query: IQuery, updateLayerTheme: boolean = true) {
        if (esriLayer.mapcoreProps) {
            esriLayer.mapcoreProps.query = query;
        }
        if (esriLayer.type === 'map-image') {
            const newSublayers: IMapcoreSubLayer[] = [];
            for (const sublayer of esriLayer.sublayers.toArray()) {
                if (sublayer.source.type === 'data-layer' && sublayer.source.dataSource.type === 'query-table') {
                    const finalQuery = this.toSqlStringService.addFilter({ ...query }, esriLayer.mapcoreProps.parentQuery);
                    (<__esri.QueryTableDataSource>sublayer.source.dataSource).query = JSON.stringify(finalQuery);
                }
                const clonedSublayer = sublayer.clone() as IMapcoreSubLayer;
                clonedSublayer.mapcoreProps = { ...sublayer.mapcoreProps };
                if (clonedSublayer.mapcoreProps.renderers && clonedSublayer.mapcoreProps.renderers.length && clonedSublayer.mapcoreProps.renderers[0].visualVariables) {
                    (clonedSublayer.renderer as any).visualVariables = clonedSublayer.mapcoreProps.renderers[0].visualVariables;
                }
                newSublayers.push(clonedSublayer);
            }
            esriLayer.sublayers.removeAll();
            esriLayer.sublayers.addMany(newSublayers);
            this.tooltipService.setupMapImageLayerPopupTemplate(esriLayer);
            if (esriLayer.mapcoreProps.isThemed && updateLayerTheme) {
                try {
                    await this.themeService.updateLayerTheme(esriLayer, false);
                    this.mapLayersSub.next();
                } catch (er) {
                    if ((<Error>er).message) {
                        this.LoggingService.Log('Layer Theme cannot be updated', er, LogLevel.Warn, false);
                    }
                }
            }
        }
        esriLayer.refresh();
    }

    updateJoinLayerColumns(esriLayer: QueryableMapCoreLayer, joinColumns: ILayerColumn[], childColumns: ILayerColumn[]) {
        if (esriLayer.mapcoreProps) {
            esriLayer.mapcoreProps.joinColumns = joinColumns;
            esriLayer.mapcoreProps.childColumns = childColumns;
        }
        esriLayer.refresh();
    }

    getExistingEsriLayerById(id: string) {
        let retVal = null;
        const layers = this.mapRef.layers.toArray();
        layers.forEach(layer => {
            if (layer.id === id && !retVal) {
                retVal = layer;
            } else if (isGroupLayer(layer)) {
                for (const sublayer of layer.layers) {
                    if (sublayer.id === id && !retVal) {
                        retVal = sublayer;
                    }
                }
            }
        });
        return retVal;
    }

    getStringifiedRenderersForLayer(layer): string {
        const rendererDefinitions = this.getRendererDefinitionsForLayer(layer);
        return JSON.stringify(rendererDefinitions);
    }

    getRendererDefinitionsForLayer(layer: IMapcoreMapImageLayer) {
        const rendererDefinitions = [];
        if (layer.sublayers && layer.sublayers.length) {
            for (const sublayer of layer.sublayers.toArray()) {
                if (sublayer.renderer) {
                    rendererDefinitions.push(this.fixRenderer(sublayer.renderer));
                }
            }
        }
        return rendererDefinitions;
    }

    fixRenderer(renderer) {
        let rend;
        if (renderer.toJSON) {
            rend = renderer.toJSON();
        } else {
            rend = renderer;
        }
        rend.type = rend.type.replace('uniqueValue', 'unique-value').replace('classBreaks', 'class-breaks');
        if (rend.field1) {
            rend.field = rend.field1;
            delete rend.field1;
        }
        if (rend.symbol) {
            this.legendService.convertSymbolJSONToSymbolDefinition(rend.symbol);
        }
        if (rend.defaultSymbol) {
            this.legendService.convertSymbolJSONToSymbolDefinition(rend.defaultSymbol);
        }
        if (rend.uniqueValueInfos) {
            for (const uniqueValueInfo of rend.uniqueValueInfos) {
                if (uniqueValueInfo.symbol) {
                    this.legendService.convertSymbolJSONToSymbolDefinition(uniqueValueInfo.symbol);
                }
            }
        }
        if (rend.classBreakInfos) {
            const minValue = rend.minValue || 0;
            delete rend.minValue;

            rend.classBreakInfos.forEach((classBreakInfo, i, arr) => {
                if (classBreakInfo.symbol) {
                    if (!classBreakInfo.minValue) {
                        classBreakInfo.minValue = i === 0 ? minValue : arr[i - 1].maxValue;
                    }
                    if (classBreakInfo.classMaxValue) {
                        classBreakInfo.maxValue = classBreakInfo.classMaxValue;
                    }
                    this.legendService.convertSymbolJSONToSymbolDefinition(classBreakInfo.symbol);
                }
            });
        }
        // fixes some issues with external layers
        if (rend.authoringInfo && rend.authoringInfo.colorRamp) {
            if (renderer.authoringInfo.colorRamp.colorRamps) {
                rend.authoringInfo.colorRamp.colorRamps.forEach(colorRamp => {
                    colorRamp = this.fixAuthoringInfoColorRamp(colorRamp);
                });
            } else {
                rend.authoringInfo.colorRamp = this.fixAuthoringInfoColorRamp(rend.authoringInfo.colorRamp);
            }
        }
        return rend;
    }

    // fixes color ramp that is not set up correctly, mainly found in some external layers
    private fixAuthoringInfoColorRamp(colorRamp) {
        switch (colorRamp.algorithm) {
            case 'esriCIELabAlgorithm':
                colorRamp.algorithm = 'cie-lab';
                break;
            case 'esriHSVAlgorithm':
                colorRamp.algorithm = 'hsv';
                break;
            case 'esriLabLChAlgorithm':
                colorRamp.algorithm = 'lab-lch';
                break;
            default:
                break;
        }
        if (colorRamp.type) {
            delete colorRamp.type;
        }
        return colorRamp;
    }

    getStringifiedGraphicsForLayer(layer: IMapcoreGraphicsLayer) {
        const graphicDefinitions = [];
        if (layer.graphics) {
            for (const graphic of layer.graphics) {
                const graphicDefinition = graphic.toJSON();
                graphicDefinition.geometry.type = graphic.geometry.type;
                this.legendService.convertSymbolJSONToSymbolDefinition(graphicDefinition.symbol);
                graphicDefinitions.push(graphicDefinition);
            }
        }
        return JSON.stringify(graphicDefinitions);
    }

    getStringifiedFeaturesForLayer(layer: IMapcoreFeatureLayer) {
        const featureDefinitions = [];
        if (layer.source && layer.source) {
            for (const feature of layer.source) {
                const featureDefinition = feature.toJSON();
                featureDefinition.geometry.type = feature.geometry.type;
                if (featureDefinition.symbol !== null) {
                    featureDefinition.symbol = featureDefinition.symbol;
                } else {
                    const layerSymbolRef = (layer.renderer as __esri.SimpleRenderer).symbol as __esri.SimpleLineSymbol | __esri.SimpleFillSymbol | __esri.SimpleMarkerSymbol;
                    featureDefinition.symbol = {
                        color: [layerSymbolRef.color.r, layerSymbolRef.color.g, layerSymbolRef.color.b, layerSymbolRef.color.a],
                        style: layerSymbolRef.style,
                        type: layerSymbolRef.type
                    };
                    if (isSymbolWithWidth(layerSymbolRef)) {
                        featureDefinition.symbol.width = layerSymbolRef.width;
                    }
                    if (isSymbolWithSize(layerSymbolRef)) {
                        featureDefinition.symbol.size = layerSymbolRef.size;
                    }
                    if (isSymbolWithOutline(layerSymbolRef)) {
                        featureDefinition.symbol.outline = {
                            color: [layerSymbolRef.outline.color.r, layerSymbolRef.outline.color.g, layerSymbolRef.outline.color.b, layerSymbolRef.outline.color.a],
                            style: layerSymbolRef.outline.style,
                            type: layerSymbolRef.outline.type,
                            width: layerSymbolRef.outline.width
                        };
                    }
                }
                this.legendService.convertSymbolJSONToSymbolDefinition(featureDefinition.symbol);
                featureDefinitions.push(featureDefinition);
            }
        }
        return JSON.stringify(featureDefinitions);
    }

    getEsriLayerByUniqueId(uniqueId: string): MapCoreLayer {
        return this.mapRef.findLayerById(uniqueId) as MapCoreLayer;
    }

    async getLayerRecordCount(layerId: number, query?: IQuery) {
        return await this.layerApi.getLayerRecordCount(layerId, query ? query : null);
    }
    async getDistinctValuesNoQuery(layerId: number, columnName: string) {
        return await this.layerApi.getDistinctValues(layerId, columnName);
    }
    async getDistinctValues(layerId: number, columnName: string, query: IQuery) {
        return await this.layerApi.getDistinctValues(layerId, columnName, query);
    }
    public async getExternalServiceProperties(layer: { url: string; name?: string; legend?: any[] }) {
        return await this.getExternalLayerJson(layer);
    }

    public async getExternalLayerJson(layer: { url: string; name?: string; legend?: any[] }) {
        const esriRequest = (await ESRI.Request) as any as ((url: string, options?: __esri.RequestOptions) => Promise<__esri.RequestResponse>);
        let response;
        try {
            response = await esriRequest(`${layer.url}?f=json`, {
                responseType: 'json'
            });
        }
        catch (e) {
            this.LoggingService.Log(`Unable to load the requested layer from ${new URL(e.details.url).host}`, e, LogLevel.Error, false);
            throw new Error(e);
        }

        const retVal: { [key: string]: any } = { ...layer };
        if (response && response.data) {
            if (!retVal.name) {
                if (response.data.documentInfo && response.data.documentInfo.Title) {
                    retVal.name = response.data.documentInfo.Title;
                }
            }
            const esriGeometryType = response.data.geometryType;
            if (response.data.drawingInfo && response.data.drawingInfo.renderer) {
                const renderer = response.data.drawingInfo.renderer;
                const symbols: any[] = [];
                if (renderer.symbol) {
                    symbols.push(renderer.symbol);
                } else if (renderer.uniqueValueInfos) {
                    renderer.uniqueValueInfos.forEach((value: any) => {
                        value.symbol.label = value.label;
                        symbols.push(value.symbol);
                    });
                }
                if (!retVal.legend) {
                    this.setLegendSymbolsForExternalLayer(retVal, symbols, esriGeometryType);
                }
            }
            for (const k in response.data) {
                if (k !== 'id') {
                    retVal[k] = response.data[k]; //Don't take the id from the external service.
                }
            }
        }
        return retVal;
    }

    public setLegendSymbolsForExternalLayer(layer: any, symbols: any[], symbolGeometryType: string) {
        let geometryType = (<any>layer).geometryType || this.getTerseGeometryType(symbolGeometryType);
        if (geometryType) { geometryType = geometryType.toLowerCase(); }
        layer.legend = [];
        layer.legendData = [];
        symbols.forEach(async (s) => {
            if (s) {
                const symbol = { ...s };
                const symbolColor = symbol.fillColor || symbol.color;
                // set symbol.outline.color to borderColor to display it in TOC
                let borderColor;
                if ((geometryType === 'polygon') && symbol.outline && symbol.outline.color) {
                    borderColor = symbol.outline.color;
                }
                layer.legend.push(symbol);
                if (symbolColor && geometryType) {
                    const esriBackgroundColor = await this.legendService.getEsriColor(symbolColor);
                    const esriBorderColor = await this.legendService.getEsriColor(borderColor);
                    if (typeof esriBackgroundColor.toHex === 'function') {
                        symbol.backgroundColor = esriBackgroundColor.toHex();
                        if (parseFloat(esriBackgroundColor.a) === 0) {
                            symbol.backgroundColor = 'none'; // Setting backgroundcolor none when Layer Opacity is zero.
                        }
                    }
                    if (borderColor) {
                        if (typeof esriBorderColor.toHex === 'function') {
                            symbol.borderColor = esriBorderColor.toHex();
                        }
                    }
                    symbol.type = geometryType;
                }
            }
        });
    }

    public async getExternalLayerSymbolData(layer: any) {

        if (layer.legend) {
            // We already have legend property present, don't get a new one. This can happen if the
            // layer symbology was already loaded through the UI, Map record, Layer record or injected
            // through an API call from a host.
            return layer;
        }

        if (layer.legendData && (<any>layer.legendData).legend) {
            // We do have a legendData.legend property, copy to legend.
            layer.legend = (<any>layer.legendData).legend;
            return layer;
        }

        // We have no legend information yet, get it from the layer.
        const result = await this.getExternalLegendJson(layer);
        // Overlay legend information with /legend data from service if it exists.
        // We want the legend symbol because it is sized independant of map scale.
        // Only if there's no legend, then set external layer symbol using renderer.symbol. Otherwise just use the legend symbol.
        if (!result.legend) {
            return await this.getExternalLayerJson(layer);
        } else {
            return result;
        }
    }

    public getTerseGeometryType(esriGeometryType: string) {
        if (esriGeometryType) {
            const geometryType = esriGeometryType.toLowerCase().replace(/esri|multi|geometry/g, '').toUpperCase();
            return geometryType.replace('POLYLINE', 'LINE');
        }
        return null;
    }

    private async getExternalLegendJson(layer: any) {

        if (layer.url.toLowerCase().indexOf('/featureserver/') > 0) {
            layer.layerType = layer.type; //Usually the legend request below sets the layerType, so since that won't get called, we set it here instead.
            return layer;
        }
        const esriRequest = (await ESRI.Request) as any;
        let legendUrl;
        let layerId;
        if (/^https?:\/\/.*\/[0-9]{1,}$/.test(layer.url)) {
            legendUrl = `${layer.url.substring(0, layer.url.lastIndexOf('/'))}/legend`;
            // tslint:disable-next-line:restrict-plus-operands
            layerId = parseInt(layer.url.substring(layer.url.lastIndexOf('/') + 1), 0);
        } else {
            legendUrl = `${layer.url}/legend`;
        }
        const response = await esriRequest(`${legendUrl}?f=json`, {
            responseType: 'json'
        });
        if (response && response.data && response.data.layers) {
            const layerResult = response.data.layers.find((r: any) => {
                return r.layerId === layerId || r.id === layerId;
            });
            layer = { ...layer, ...layerResult };
            if (layerResult && layerResult.legend) {
                layer.displayName = ((layer.displayName) ? layer.displayName : layerResult.layerName);
            }
        }
        return layer;
    }

    public async addSavedGraphicsLayer(layerSettings: ILayerSettings, layer: ILayer, mapcoreProps: IMapcoreProps = null) {
        if (layer.graphicsJSON) {
            const graphics = JSON.parse(layer.graphicsJSON);
            const graphicsLayer = await this.addGraphicsLayer({
                graphics, title: layer.name, visible: layerSettings.visible, opacity: layerSettings.opacity, minScale: mapcoreProps.originalMinScale, maxScale: mapcoreProps.originalMaxScale
            });
            if (mapcoreProps) {
                (<any>graphicsLayer).mapcoreProps = mapcoreProps;
            }
            return graphicsLayer;
        }
    }

    public async addGraphicsLayer(properties?: __esri.GraphicsLayerProperties | { [key: string]: any }, mapcoreProps: IMapcoreProps = null) {
        const GraphicsLayer = await ESRI.GraphicsLayer;
        let hasPictureMarkerSymbol = false;
        if (properties.graphics) {
            for (const g of properties.graphics) {
                if (g.symbol.type === 'esriPMS') {
                    g.symbol.type = 'picture-marker';
                    hasPictureMarkerSymbol = true;
                }
            }
        }
        const layer = new GraphicsLayer(properties);
        if (mapcoreProps) {
            (<IMapcoreGraphicsLayer>layer).mapcoreProps = mapcoreProps;
        }
        if (hasPictureMarkerSymbol) {
            this.fixPictureSymbols(layer.graphics.map(g => g.symbol).toArray());
        }
        this.tooltipService.setupGraphicsLayerPopupTemplate(layer);
        this.mapRef.add(layer);
        return layer;
    }

    public zoomToGraphic(graphic) {
        this.zoomToTarget(graphic.geometry);
    }

    // Zooms to the extent of the target.
    // If the total extent size of the target's geometries is above the set threshold, it zooms to that extent.
    // otherwise, a default zoom is applied to prevent zooming in too close.
    public async zoomToTarget(target, heightOrWidthThreshold: number = 2000, zoomThreshold: number = 14) {
        let geometries = [];
        if (target.length) {
            geometries = target;
        } else {
            geometries.push(target);
        }
        let totalExtent;
        geometries = geometries.filter(x => x);
        for (const geometry of geometries) {
            if (!totalExtent && geometry.extent) {
                totalExtent = geometry.extent.clone();
            } else if (geometry.extent) {
                totalExtent.union(geometry.extent);
            }
        }
        let maxWidthOrHeight = 0;
        if (totalExtent) {
            maxWidthOrHeight = Math.max(maxWidthOrHeight, totalExtent.height, totalExtent.width);
        }
        const goToTarget = (maxWidthOrHeight > heightOrWidthThreshold) ? geometries : { target: geometries, zoom: zoomThreshold };

        await this.mapView.goTo(goToTarget, { animate: true });
    }

    public moveLayerToTheTop(layer: __esri.Layer) {
        this.mapRef.reorder(layer, this.mapRef.allLayers.length - 1);
    }

    public moveLayerToTheBottom(layer: __esri.Layer) {
        this.mapRef.reorder(layer, 0);
    }

    public moveLayerAbove(layer: __esri.Layer, lowerLayer: __esri.Layer) {
        const lowerLayerIndex = this.mapRef.layers.indexOf(lowerLayer);
        if (lowerLayerIndex > -1) {
            this.mapRef.reorder(layer, lowerLayerIndex + 1);
        } else {
            this.moveLayerToTheTop(layer);
        }
    }

    public moveLayerBelow(layer: __esri.Layer, higherLayer: __esri.Layer) {
        const higherLayerIndex = this.mapRef.layers.indexOf(higherLayer);
        if (higherLayerIndex > -1) {
            this.mapRef.reorder(layer, higherLayerIndex);
        } else {
            this.moveLayerToTheBottom(layer);
        }
    }

    public async convertGeometry(geometry: string[][]): Promise<Geometry[]> {
        const [Polyline, Polygon] = await ESRI.loadModules('esri/geometry/Polyline', 'esri/geometry/Polygon');
        const geometryJSON = geometry.flat().map(a => {
            if (a === null) {
                return;
            }
            if (a.includes('POINT')) {
                a = a.replace('POINT (', '');
                a = a.replace(')', '');
                const coords = a.split(' ');
                return {
                    type: 'point', x: parseFloat(coords[0]), y: parseFloat(coords[1]), spatialReference: { wkid: 102100 }
                };
            } else if (a.includes('LINESTRING')) {
                a = a.replace(/MULTI|LINESTRING|\(|\)/gi, '');
                const b = a.split(',');
                const coordsArray = b.map((c) => {
                    const coords = c.trim().split(' ');
                    return [parseFloat(coords[0]), parseFloat(coords[1])];
                });
                return new Polyline({
                    paths: [coordsArray], spatialReference: { wkid: 102100 }
                });
            } else if (a.includes('MULTIPOLYGON')) {
                a = a.replace('MULTIPOLYGON ', '');
                const arr = a.split(')), (');
                const polygons = arr.map((item) => {
                    const b = item.split('(');
                    let coordsArray = b.map((c) => {
                        if (!c) { return; }
                        const d = c.split(',');
                        return d.map((e: string) => {
                            e = e.trim();
                            if (!e) { return; }
                            e = e.replace(/\(/gi, '');
                            e = e.replace(/\)/gi, '');
                            const coords = e.trim().split(' ');
                            return [parseFloat(coords[0]), parseFloat(coords[1])];
                        }).filter(v => !!v);
                    });
                    coordsArray = coordsArray.filter(v => !!v);
                    const polygon = new Polygon({
                        rings: coordsArray, spatialReference: { wkid: 102100 }
                    });
                    if (!polygon.isClockwise(polygon.rings)) {
                        for (let i = 0; i < polygon.rings.length; i++) {
                            polygon.rings[i] = polygon.rings[i].reverse();
                        }
                    }
                    return polygon;
                });
                return polygons;
            } else if (a.includes('POLYGON')) {
                a = a.replace('POLYGON (', '');
                a = a.replace(')', '');
                const b = a.split('(');
                let coordsArray = b.map((c) => {
                    if (!c) { return; }
                    const d = c.split(',');
                    return d.map((e) => {
                        e = e.replace('(', '');
                        e = e.replace(')', '');
                        const coords = e.trim().split(' ');
                        return [parseFloat(coords[0]), parseFloat(coords[1])];
                    });
                });
                coordsArray = coordsArray.filter(v => !!v);
                const polygon = new Polygon({
                    rings: coordsArray, spatialReference: { wkid: 102100 }
                });
                if (!polygon.isClockwise(polygon.rings)) {
                    for (let i = 0; i < polygon.rings.length; i++) {
                        polygon.rings[i] = polygon.rings[i].reverse();
                    }
                }
                return polygon;
            }
        });
        return geometryJSON.filter(v => !!v).reduce((x, value) => x.concat(value), []);
    }

    public turnOnLayerIds(layerIds: number[]) {
        for (const layer of Object.values(this.mapLayers).flat()) {
            if (layer.mapcoreProps && layer.mapcoreProps.idFromSavedMap && layerIds.indexOf(layer.mapcoreProps.idFromSavedMap) >= 0) {
                layer.mapcoreProps.visible = true;
                layer.visible = true;
                this.tooltipService.setPopupTemplateVisibility(layer);
                layer.refresh();
            }
        }
        this.mapLayersSub.next(this.mapLayers);
    }

    public removeAoiLayers() {
        const aoiLayers = this.getAoiLayers();
        if (this.mapRef) {
            this.mapRef.removeMany(aoiLayers);
        }
    }

    public getAoiLayers() {
        return Object.values(this.mapLayers).flat().filter(l => {
            return (<IMapcoreProps>(l as any).mapcoreProps).isAOI;
        });
    }

    public getMapLayers() {
        return Object.values(this.mapLayers).flat();
    }
    /**
     * Return a list of temporary layers use internally by MapCore.
     */
    public getTempLayers(): MapCoreLayer[] {
        return Object.values(this.mapLayers).flat().filter(x => this.isTempLayer(x));
    }

    /**
     * Return true if a layer is for internal MapCore temporary use only.
     */
    public isTempLayer(layer: MapCoreLayer): boolean {
        return (layer.mapcoreProps && layer.mapcoreProps.hideInLegend);
    }

    public async getLayerFeatures(layer: IMapcoreMapImageLayer, outFields: string[] = ['*']) {
        let retval: any[] = null;
        if (layer && layer.mapcoreProps && layer.mapcoreProps.geometryTypes && layer.mapcoreProps.geometryTypes.includes('line')) {

            const cloneSublayer = layer.mapcoreProps.geometryTypes.length > 1 ? layer.sublayers.toArray()[1].clone() : layer.sublayers.toArray()[0].clone();
            const query = cloneSublayer.createQuery();
            query.outFields = outFields;
            query.returnGeometry = true;

            const result = await cloneSublayer.queryFeatures(query);
            if (result && result.features) {
                const wmu = await ESRI.WebMercatorUtils;
                for (const feature of result.features) {
                    const geometry = feature.geometry as __esri.Polyline & { startLatLong: number[]; endLatLong: number[] };
                    geometry.startLatLong = wmu.xyToLngLat(geometry.paths[0][0][0], geometry.paths[0][0][1]).reverse();

                    const segLength = geometry.paths[0].length - 1;
                    geometry.endLatLong = wmu.xyToLngLat(geometry.paths[0][segLength][0], geometry.paths[0][segLength][1]).reverse();

                }
                retval = result.features;
            }
        }
        return retval;
    }

    public getLayerType(layer) {
        if (layer.type === 'group') {
            return 'group';
        } else if (layer && layer.sublayers !== undefined && layer.sublayers.length > 0) {
            return 'dynamic';
        } else if (layer.type === 'feature' || layer === 'feature') {
            return 'feature';
        } else if (layer.graphics !== null) {
            return 'graphics';
        }
    }

    public getObjectIdColumnName(mapcoreProps: IMapcoreProps) {
        const columnIdentifierIdForObjectId = 10;
        if (mapcoreProps && mapcoreProps.columns) {
            const filterResults = mapcoreProps.columns.filter(x => x.columnIdentifierId === columnIdentifierIdForObjectId);
            if (filterResults.length > 0) {
                return filterResults[0].name.toLowerCase();
            }
        }
        return 'objectid';
    }

    public async deletelayer(layerId) {
        return await this.layerApi.delete(layerId);
    }

    public refreshLayers() {
        this.mapLayers.dynamic.forEach(layer => layer.refresh());
        this.mapLayers.feature.forEach(layer => layer.refresh());
        this.mapLayers.mapImage.forEach(layer => layer.refresh());
        this.layersRefreshed.emit();
    }

    /**
     * Get the layers geometry or request it from the server if not available client-side.
     */
    public async getLayerGeometry(layer: MapCoreLayer, first: number = 1000): Promise<__esri.Geometry | __esri.Geometry[]> {
        let geometry: __esri.Geometry | __esri.Geometry[];
        switch (layer.type) {
            case 'map-image':
                const layerData = await this.layerApi.getData(layer.mapcoreProps.parentId, ['GEOMETRY'], layer.mapcoreProps.query, first);
                geometry = await this.convertGeometry(layerData);
                break;
            case 'feature':
                //buffering a feature layer is a work in progress.
                if (layer.url) {
                    const query = layer.createQuery();
                    query.returnGeometry = true;
                    query.where = '1=1';
                    QueryTask = await ESRI.QueryTask;
                    const queryTask = new QueryTask({
                        url: `${layer.url}/${layer.layerId}`
                    });
                    const response = await queryTask.execute(query);
                    geometry = response.features.map(f => f.geometry);
                } else {
                    geometry = layer.source.toArray().map(s => s.geometry);
                }
                break;
            case 'graphics':
                geometry = layer.graphics.toArray().map(i => i.geometry);
                break;
            default:
                geometry = null;
                break;
        }
        return geometry;
    }

    /**
     * Get the layers geometry filtering out geometries that do not match the primary geometry.
     * Use this function for processing that expects a layer to have only one type of geometry.
     * Our export shapefile using export.py expects only one type of geometry.
     * @param layer Layer to return geometry for
     * @param first Limit number of geometries returned.
     * @returns Array of geometries with non-matching geometries discarded.
     */
    public async getLayerGeometrySingleType(layer: MapCoreLayer, first: number = 1000): Promise<__esri.Geometry | __esri.Geometry[]> {
        const geometry = await this.getLayerGeometry(layer, first);
        if (Array.isArray(geometry)) {
            return geometry.filter(g => g.type === geometry[0].type);
        }
        return geometry;
    }

    async getLayerGraphicsWithFormattedSymbol(layer: MapCoreLayer, symbol: any) {
        Graphic = await ESRI.Graphic;
        let graphics: __esri.Graphic[];
        const geometries = await this.getLayerGeometry(layer);
        if (geometries && Array.isArray(geometries)) {
            graphics = geometries.map(g => {
                return new Graphic({
                    geometry: g,
                    symbol
                });
            });
        }

        const graphicDefinitions = [];
        if (graphics) {
            for (const graphic of graphics) {
                const graphicDefinition = graphic.toJSON();
                graphicDefinition.geometry.type = graphic.geometry.type;
                this.legendService.convertSymbolJSONToSymbolDefinition(graphicDefinition.symbol);
                graphicDefinitions.push(graphicDefinition);
            }
        }
        return graphicDefinitions;
    }

    public async getUserLayersFolders(userId: number, applicationId?: number) {
        const props = `id, folders(category:"layers", applicationId:${applicationId}) {id, applicationId, name, parentUserFolderId, layers{id} }`;
        return await this.layerApi.getUserLayersFolders(userId, props);
    }
    public async getDrawLayersFolders(userId: number, applicationId?: number) {
        const props = `id, folders(category:"drawing-layers", applicationId:${applicationId}) {id, applicationId, name, parentUserFolderId, layers{id} }`;
        return await this.layerApi.getUserLayersFolders(userId, props);
    }

    public async saveLayerToUserFolder(layerId: number, folderId: number) {
        return await this.layerApi.saveLayerToUserFolder(layerId, folderId);
    }

    public async emailAboutSharedLayer(layerId: number, layerName: string, emailAddresses: string[], emailSubject: string, emailBody: string) {
        emailBody = this._buildMessage(emailBody, layerId, layerName);

        return await this.layerApi.emailAboutSharedLayer(emailAddresses, emailSubject, emailBody);
    }

    private _buildMessage(message: string, layerId: number, layerName: string) {
        const appName = environment.appName ? environment.appName : '';
        const layerUrl = this.getLayerUrl(layerId);
        let template = `I\'ve shared a new layer with you from ${appName} named ${layerName}. You can view the layer in ${appName} by going to the Layers dialog.\n\n ${layerUrl}`;
        if (message) {
            template += `\n\nMessage from user: ${message}`;
        }
        return template;
    }

    public getLayerUrl(layerId: number): string {
        const baseUrl = environment.deepLinkBaseUrl || window.location.href.split('?')[0];
        return `${baseUrl}?layer-id=${layerId}`;
    }
    public applyCyfyLayerFilterTransform(paramConfig: IMapQueryConfiguration[], mapcoreProps: IMapcoreProps) {
        for (const cyfyConfig of paramConfig.filter(config => config.type === 'CYFY')) {
            if (cyfyConfig.layers.indexOf(mapcoreProps.parentId) > -1) {
                cyfyConfig.value = 'fy';
                this.updateLayerName(cyfyConfig, mapcoreProps);
            }
        }
    }

    public updateLayerName(cyfyConfig: IMapQueryConfiguration, mapcoreProps: IMapcoreProps) {
        const isRenameLayerRegex = /(XXXX|(C|F)Y\d\d\d\d)/;
        let yearOffset;
        if (mapcoreProps.query.subqueries) {
            if (mapcoreProps.query.subqueries.find(x => x.field === 'fy_plan_year_no')) {
                yearOffset = mapcoreProps.query.subqueries.find(x => x.field === 'fy_plan_year_no');
            } else if (mapcoreProps.query.subqueries.find(x => x.field === 'cy_plan_year_no')) {
                yearOffset = mapcoreProps.query.subqueries.find(x => x.field === 'cy_plan_year_no');
            }
        } else if (mapcoreProps.query.field && (mapcoreProps.query.field === 'fy_plan_year_no' || mapcoreProps.query.field === 'cy_plan_year_no')) {
            yearOffset = mapcoreProps.query;
        }
        if (yearOffset && cyfyConfig.value === 'fy') {
            yearOffset.field = 'fy_plan_year_no';
        } else if (yearOffset && cyfyConfig.value === 'cy') {
            yearOffset.field = 'cy_plan_year_no';
        }
        if (mapcoreProps.parentQuery && mapcoreProps.parentQuery.field && yearOffset && (cyfyConfig.value === 'fy' || cyfyConfig.value === 'cy')) {
            mapcoreProps.parentQuery.field = yearOffset.field;
        }
        if (isRenameLayerRegex.test(mapcoreProps.name) && yearOffset) {
            const yearToDisplay = this.generateYearDisplay(cyfyConfig.value, yearOffset.value);
            mapcoreProps.name = mapcoreProps.name.replace(isRenameLayerRegex, yearToDisplay);
        }
    }

    public getFiscalYear(): number {
        const month = new Date().getMonth() + 1;
        const year = new Date().getFullYear();
        if (month >= 7) {
            return year;
        } else {
            return year - 1;
        }
    }

    public getCurrentYear(): number {
        return new Date().getFullYear();
    }

    public getRamdonIdForEsriLayer() {
        return `${uuid.v1(null, 0, 0).substring(0, 7)}-layer`;
    }

    private generateYearDisplay(yearType: string, offset: string): string {
        let displayString = '', year: number;

        if (yearType === 'cy') {
            displayString += 'CY';
            year = new Date().getFullYear();
        } else {
            displayString += 'FY';
            year = this.getFiscalYear();
        }

        // year = current year + offset - 1
        // current year is offset = 1
        displayString += (year + parseInt(offset, 0) - 1).toString();

        return displayString;
    }

    private applyCurrentFYTransformation(mapcoreProps: IMapcoreProps): void {
        const currentYear = this.getCurrentYear();
        const lastYear = currentYear - 1;

        const isCurrentFYRenameLayerRegex = /FYZZZZ/;
        if (isCurrentFYRenameLayerRegex.test(mapcoreProps.name)) {
            mapcoreProps.name = mapcoreProps.name.replace(isCurrentFYRenameLayerRegex, `FY${this.getFiscalYear()}`);
        }
        //this is for Visualization of Period Plan - Calendar Year map. CYZZZZ is intentionally converted to FY{currentYear}
        const isCurrentCYRenameLayerRegex = /CYZZZZ/;
        if (isCurrentCYRenameLayerRegex.test(mapcoreProps.name)) {
            mapcoreProps.name = mapcoreProps.name.replace(isCurrentCYRenameLayerRegex, `FY${currentYear}`);
        }
        //this is for Visualization of Period Plan - Calendar Year map. CYZZZY is intentionally converted to FY{lastYear}
        const isLastCYRenameLayerRegex3 = /CYZZZY/;
        if (isLastCYRenameLayerRegex3.test(mapcoreProps.name)) {
            mapcoreProps.name = mapcoreProps.name.replace(isLastCYRenameLayerRegex3, `FY${lastYear}`);
        }
    }

    public getLayerForSaveMap(layer: MapCoreLayer, isNew = false) {
        return this.getLayerForSave(layer, null, null, LayerTypeId.Map, isNew, true);
    }

    public getLayerForSave(layer: MapCoreLayer, name?: string, description?: string, typeId?: number, isNew = false, isMapLayer = false) {
        if (layer.type === 'group') {
            return {
                isGroupLayer: true,
                isGroupParent: true,
                groupParentUniqueId: layer.id,
                splitThemeLabel: layer.mapcoreProps.splitThemeLabel,
                splitThemeJSON: layer.mapcoreProps.splitTheme ? JSON.stringify(this.splitThemeService.getThemeForSave(layer.mapcoreProps.splitTheme)) : undefined
            } as ILayer;
        }

        const mapcoreProps = { ...layer.mapcoreProps };
        mapcoreProps.originalMinScale = layer.minScale;
        mapcoreProps.originalMaxScale = layer.maxScale;

        let id = null;
        if (!isNew) {
            // If saving layer and there's an existing ID
            if (!isMapLayer && mapcoreProps.idFromSavedLayer) {
                id = mapcoreProps.idFromSavedLayer;
            } else {
                id = mapcoreProps.id;
            }
        }

        const mapcoreLayer: ILayer = {
            id,
            name: name || mapcoreProps.name,
            parentId: mapcoreProps.parentId,
            typeId: typeId || mapcoreProps.typeId,
            description,
            queryJSON: this.modifyQueryForSave(mapcoreProps),
            url: mapcoreProps.url,
            originalMinScale: mapcoreProps.originalMinScale,
            originalMaxScale: mapcoreProps.originalMaxScale,
            isAOI: mapcoreProps.isAOI,
            splitThemeLabel: mapcoreProps.splitThemeLabel
        };

        if (isMapLayer) {
            mapcoreLayer.visible = layer.visible ? null : layer.visible;
            mapcoreLayer.opacity = layer.opacity === 1 ? null : layer.opacity;

            if (layer.mapcoreProps.groupParent) {
                mapcoreLayer.groupParentName = layer.mapcoreProps.groupParent.name;
                mapcoreLayer.groupParentUniqueId = layer.mapcoreProps.groupParent.groupId;
                mapcoreLayer.isGroupLayer = true;
                mapcoreLayer.isGroupLayerExpanded = layer.mapcoreProps.groupParent.isOpen;
            }
        }

        if (!mapcoreLayer.id) {
            delete mapcoreLayer.id;
        }

        this.modifyRenderersForSave(layer, mapcoreLayer);
        if (mapcoreProps.labelingInfo && mapcoreProps.labelingInfo.length) {
            mapcoreLayer.labelingInfoJSON = JSON.stringify(mapcoreProps.labelingInfo);
        }
        if (mapcoreProps.routingDetail) {
            mapcoreLayer.routingDetailJSON = JSON.stringify(mapcoreProps.routingDetail);
        }
        return mapcoreLayer;
    }

    private modifyQueryForSave(mapcoreProps: IMapcoreProps) {
        let queryJSON = null;
        let query: ISaveableQuery;
        if (mapcoreProps.query) {
            query = { ...mapcoreProps.query };
            if (mapcoreProps.spatialQueryParams) {
                query.spatialQueryParams = mapcoreProps.spatialQueryParams;
            }
            if (mapcoreProps.parentQuery) {
                query.parentQuery = mapcoreProps.parentQuery;
            }
            queryJSON = JSON.stringify(query);
        } else {
            const saveParams: ISaveableQuery = null;
            if (mapcoreProps.spatialQueryParams) {
                saveParams.spatialQueryParams = mapcoreProps.spatialQueryParams;
            }
            if (mapcoreProps.parentQuery) {
                saveParams.parentQuery = mapcoreProps.parentQuery;
            }
            queryJSON = JSON.stringify(saveParams);
        }
        return queryJSON;
    }

    private modifyRenderersForSave(layer: MapCoreLayer, mapcoreLayer: ILayer) {
        const renderers = [{ type: 'opacity', value: layer.opacity }];
        if (layer.type === 'feature') {
            if (mapcoreLayer.url) {
                mapcoreLayer.url = `${layer.url}/${layer.layerId}`;
            } else {
                mapcoreLayer.graphicsJSON = this.getStringifiedFeaturesForLayer(layer);
            }
            renderers.push(layer.renderer.toJSON());
        }
        if (layer.type === 'graphics') {
            mapcoreLayer.graphicsJSON = this.getStringifiedGraphicsForLayer(layer);
        }
        if (layer.type === 'map-image') {
            renderers.push(...layer.sublayers.toArray().map(sublayer => sublayer.renderer.toJSON()));
        }
        mapcoreLayer.renderersJSON = JSON.stringify(renderers);
    }

    public async getBaseParentId(parentId: number) {
        return await this.layerApi.getBaseParentId(parentId);
    }

    public fixPictureSymbols(symbols: __esri.Symbol[]) {
        for (const symbol of symbols) {
            if (isPictureMarkerSymbol(symbol) && typeof (symbol.url) === 'object') {
                //Esri contructor for loaded in PictureMarkerSymbol JSON puts the values into an object in url attr, this code below is a hack to assign those properties back to their correct locations
                const symbolObject = symbol.url as { angle: number; height: number; width: number; imageData: string; xoffset: number; yoffset: number; url?: string };

                symbol.angle = symbolObject.angle;
                symbol.height = symbolObject.height;
                symbol.width = symbolObject.width;
                symbol.xoffset = symbolObject.xoffset;
                symbol.yoffset = symbolObject.yoffset;
                symbol.url = symbolObject.url || symbolObject.imageData;
                if (!symbol.url.startsWith('data:image/png;base64,') && !symbol.url.startsWith('http')) {
                    symbol.url = `data:image/png;base64,${symbol.url}`;
                }
            }
        }
    }

    async identifyMapImagelayers(mapPoint: __esri.Point, checkLayerVisibility, includeChildTableDictionary, queryTolerance = null, additionalQueryDistance = null): Promise<IIdentifyResult> {
        const mapImageLayers = (<IMapcoreMapImageLayer[]>this.mapView.map.allLayers.toArray()).filter(layer =>
            layer.type === 'map-image'
            && (checkLayerVisibility
                ? (layer.visible && this.layerIdentifyEnabled(layer)) || this.layerIdentifyNonVisibleEnabled(layer)
                : true
            )
            && (!layer.maxScale || this.mapView.scale > layer.maxScale)
            && (!layer.minScale || this.mapView.scale < layer.minScale)
        );
        const sublayerArrays = mapImageLayers.map(x => x.sublayers.toArray());
        const sublayers = [...sublayerArrays.flat()];
        let results: IFeatureResult[] = [];
        const fields: { [key: string]: __esri.Field[] } = {};

        const baseParentIdDict = new Map<number, number>();
        const parentIdToChildTableDict = new Map<number, ILayerTableChild[]>();

        if (includeChildTableDictionary) {
            const parentIds = sublayers.map(sublayer => {
                let retVal: number = null;
                const mapcoreProps = this.getSourceMapcoreProps(sublayer);
                if (mapcoreProps && mapcoreProps.parentId) {
                    retVal = mapcoreProps.parentId;
                }
                return retVal;
            }).filter(x => x);

            if (parentIds.length) {
                const setChildTablesToDictionary = async (parentId: number, key?: number) => {
                    const layerChildTables = await this.getLayerChildTables(parentId);
                    if (layerChildTables && layerChildTables.length) {
                        parentIdToChildTableDict.set(key || parentId, layerChildTables);
                    }
                };
                await Promise.all(parentIds.map(async (parentId) => {
                    if (!parentIdToChildTableDict.has(parentId) && !baseParentIdDict.has(parentId)) {
                        await setChildTablesToDictionary(parentId);
                    }
                    if (!parentIdToChildTableDict.has(parentId) && baseParentIdDict.has(parentId) && baseParentIdDict.get(parentId)) {
                        await setChildTablesToDictionary(baseParentIdDict.get(parentId), parentId);
                    } else if (!baseParentIdDict.has(parentId)) {
                        const baseParentId = await this.getBaseParentId(parentId);
                        baseParentIdDict.set(parentId, baseParentId);
                        if (baseParentId) {
                            await setChildTablesToDictionary(baseParentIdDict.get(parentId), parentId);
                        }
                    }
                }));
            }
        }

        await Promise.all(sublayers.map(async (sublayer) => {
            const mapcoreProps = this.getSourceMapcoreProps(sublayer);
            if (mapcoreProps) {
                const query = sublayer.createQuery();
                query.geometry = mapPoint;
                query.outFields = mapcoreProps.columns ? mapcoreProps.columns.map(x => x.name) : ['*'];
                query.spatialRelationship = 'intersects';
                query.returnGeometry = true;
                this.setTolerance(query, queryTolerance, additionalQueryDistance);

                const queryResult = await sublayer.queryFeatures(query);

                if (queryResult.features) {
                    const existingField = fields[sublayer.parent.id];
                    if (existingField) {
                        existingField.push(...queryResult.fields.filter(f => !existingField.find(ef => ef.name === f.name)));
                    } else {
                        fields[sublayer.parent.id] = queryResult.fields;
                    }

                    results = results.concat(queryResult.features.reverse().map((feature: IFeatureResult) => ({
                        attributes: { ...feature.attributes },
                        geometry: feature.geometry.clone(),
                        layer: sublayer.parent,
                        sourceLayer: feature.sourceLayer,
                        layerName: mapcoreProps.name,
                        childTables: this.getChildTableResult(includeChildTableDictionary, mapcoreProps, parentIdToChildTableDict)
                    } as IFeatureResult)));
                }
            }
        }));

        return { features: results, fields };
    }

    public getSourceMapcoreProps(sourceLayer) {
        let retVal: IMapcoreProps = null;
        if (sourceLayer) {
            if (sourceLayer.mapcoreProps && (sourceLayer.mapcoreProps.columns || sourceLayer.type === 'feature' || sourceLayer.type === 'graphics')) {
                retVal = sourceLayer.mapcoreProps;
            } else if (sourceLayer.parent && sourceLayer.parent.mapcoreProps) {
                retVal = sourceLayer.parent.mapcoreProps;
            }
        }
        return retVal;
    }

    public layerIdentifyNonVisibleEnabled(layer) {
        const mapcoreProps = this.getSourceMapcoreProps(layer);
        /* tslint:disable-next-line:no-boolean-literal-compare */
        return mapcoreProps.identifyNonVisibleEnabled === true;
    }

    public layerIdentifyEnabled(layer) {
        const mapcoreProps = this.getSourceMapcoreProps(layer);
        /* tslint:disable-next-line:no-boolean-literal-compare */
        return mapcoreProps ? mapcoreProps.identifyEnabled !== false : false;
    }

    public getTolerance(tolerance) {
        const inchesInFoot = 12;
        const ppi = 72;
        return Math.round(this.mapView.scale / ppi / inchesInFoot * tolerance);
    }

    public setTolerance(query: __esri.supportQueryProperties | __esri.supportQuery, tolerance = null, additionalDistance: number = null) {
        if (tolerance === null) {
            tolerance = 5;
        }

        if (additionalDistance === null) {
            additionalDistance = 0;
        }

        query.distance = Math.max(0, this.getTolerance(tolerance) + additionalDistance);
        query.units = 'feet';
    }

    public saveClassBreakLabels(layer: IMapcoreMapImageLayer) {
        layer.mapcoreProps.classBreakLabels = this.themeService.generateClassBreakLabels(layer);
    }

    private getChildTableResult(includeChildTableDictionary, mapcoreProps, parentIdToChildTableDict) {
        let result;

        if (includeChildTableDictionary) {
            const childTables = parentIdToChildTableDict.get(mapcoreProps.parentId);

            if (childTables) {
                result = childTables.map(x => ({ ...x }));
            }
        }

        return result;
    }
}

interface ISimpleRendererSublayerProperties extends __esri.SublayerProperties {
    renderer: __esri.SimpleRendererProperties | __esri.ClassBreaksRendererProperties | __esri.UniqueValueRendererProperties;
}

interface IFieldsDictionary {
    [key: string]: __esri.Field[];
}

interface IFeatureResult extends Partial<__esri.Graphic> {
    layerName?: string;
    childTables?: ILayerTableChild[];
    attributes: { [key: string]: string | number };
    sourceLayer?: IMapcoreSubLayer;
}

interface IIdentifyResult {
    features: IFeatureResult[];
    fields: IFieldsDictionary;
}
